/**
 * @fileoverview Present hierarchical folder navigation over caller-supplied documents and folders. The parent owns selected identity, data loading, and mutations; the tree delegates navigation and contextual actions. `DocumentFileExplorerController.js` connects common explorer interaction without making displayed hierarchy the storage authority.
 */

import React from 'react';
import AppIcon from '../shared/AppIcon';
import './FileExplorerTree.css';



function FileExplorerTree({
  nodes = [],
  getKey,
  getLabel,
  getMeta,
  getChildren,
  getAriaLabel,
  selectedKey = '',
  expandedKeys = new Set(),
  forceExpanded = false,
  onToggle,
  onOpen,
  getRowProps,
  renderTrailing,
  ariaLabel = 'Folders',
  className = '',
  emptyMessage = 'No folders',
}) {
  const renderNodes =

  (items, depth = 0, ancestors = new Set()) => items.map(

  (node) => {
    const key = String(getKey(node));
    if (!key || ancestors.has(key)) return null;
    const label = getLabel(node);
    const children = getChildren(node) || [];
    const hasChildren = children.length > 0;
    const expanded = hasChildren && (forceExpanded || expandedKeys.has(key));
    const selected = selectedKey === key;
    const { className: rowClassName = '', ...rowProps } = getRowProps?.(node) || {};
    const nextAncestors = new Set(ancestors);
    nextAncestors.add(key);
    return (
      <div
        className={`file-explorer-tree__item ${selected ? 'is-selected' : ''}`}
        role="treeitem"
        aria-selected={selected}
        aria-expanded={hasChildren ? expanded : undefined}
        key={key}>
        <div
          {...rowProps}
          className={`file-explorer-tree__item-row ${rowClassName}`.trim()}
          style={{ '--file-explorer-depth': depth }}
          onClick={(event) => {
            if (event.target.closest?.('.file-explorer-tree__disclosure')) return;
            onOpen(node);
          }}>
          <button
            type="button"
            className={`file-explorer-tree__disclosure ${hasChildren ? '' : 'is-empty'}`}
            disabled={!hasChildren}
            tabIndex={hasChildren ? 0 : -1}
            aria-label={hasChildren ? `${expanded ? 'Collapse' : 'Expand'} folder ${label}` : undefined}
            onClick={() => hasChildren && onToggle?.(key, !expanded)}>
            {hasChildren && <AppIcon icon={expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'} aria-hidden="true" />}
          </button>
          <button
            type="button"
            className="file-explorer-tree__open"
            aria-label={getAriaLabel ? getAriaLabel(node) : `Open folder ${label}`}
            onKeyDown={

            (event) => {
              if (!hasChildren || forceExpanded) return;
              if (event.key === 'ArrowRight' && !expanded) {
                event.preventDefault();
                onToggle?.(key, true);
              } else if (event.key === 'ArrowLeft' && expanded) {
                event.preventDefault();
                onToggle?.(key, false);
              }
            }}>
            <span className="file-explorer-tree__folder" aria-hidden="true">
              <AppIcon icon={selected || expanded ? 'pi pi-folder-open' : 'pi pi-folder'} />
            </span>
            <span className="file-explorer-tree__copy">
              <strong title={label}>{label}</strong>
              {getMeta && <small>{getMeta(node)}</small>}
            </span>
          </button>
          {renderTrailing && <span className="file-explorer-tree__trailing">{renderTrailing(node)}</span>}
        </div>
        {expanded && (
          <div role="group" className="file-explorer-tree__children">
            {renderNodes(children, depth + 1, nextAncestors)}
          </div>
        )}
      </div>
    );
  });

  return (
    <div className={`file-explorer-tree ${className}`.trim()} role="tree" aria-label={ariaLabel}>
      {nodes.length > 0 ? renderNodes(nodes) : <div className="file-explorer-tree__empty">{emptyMessage}</div>}
    </div>
  );
}

export default FileExplorerTree;
